// Data hooks of the transmittals screens (GEDX-FE-02): the paged list, one transmittal, the PDF
// link, the in-app acknowledgement, the diffusion history of a document and the sending (which
// carries an Idempotency-Key, so a double click never numbers two transmittals).
import {
  getTransmittalsControllerDiffusionQueryKey,
  getTransmittalsControllerGetQueryKey,
  getTransmittalsControllerListQueryKey,
  transmittalsControllerAcknowledge,
  transmittalsControllerDiffusion,
  transmittalsControllerGet,
  transmittalsControllerList,
  transmittalsControllerPdfUrl,
  useSpacePartnersControllerMembers,
  type CreateTransmittalDto,
  type TransmittalDtoOutput,
} from '@batiflow/api-client';
import { TRANSMITTAL_LIST_MAX } from '@batiflow/contracts';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useRuntime } from '@/app/providers/runtime-provider';

const listPrefix = (spaceId: string): string => `/api/v1/spaces/${spaceId}/transmittals`;

/** The transmittals of a space, newest first, a page at a time. */
export function useTransmittalPages(spaceId: string) {
  return useInfiniteQuery({
    queryKey: [...getTransmittalsControllerListQueryKey(spaceId), 'pages'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      transmittalsControllerList(
        spaceId,
        { limit: TRANSMITTAL_LIST_MAX, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** One transmittal with its items and recipients; nothing is read while `transmittalId` is null. */
export function useTransmittal(spaceId: string, transmittalId: string | null) {
  return useQuery({
    queryKey: getTransmittalsControllerGetQueryKey(spaceId, transmittalId ?? ''),
    queryFn: ({ signal }) => transmittalsControllerGet(spaceId, transmittalId ?? '', signal),
    enabled: transmittalId !== null,
  });
}

/** The transmittals that carried a version of a document. */
export function useDiffusion(spaceId: string, documentId: string) {
  return useQuery({
    queryKey: getTransmittalsControllerDiffusionQueryKey(spaceId, documentId),
    queryFn: ({ signal }) => transmittalsControllerDiffusion(spaceId, documentId, signal),
  });
}

/** A 60-second link to the transmittal PDF (`preparing` until the worker has stored it). */
export function useTransmittalPdf(spaceId: string, transmittalId: string) {
  return useMutation({
    mutationFn: () => transmittalsControllerPdfUrl(spaceId, transmittalId),
  });
}

/** Everything a sending or an acknowledgement changes: lists, details and diffusion histories. */
export async function refreshTransmittals(queryClient: QueryClient, spaceId: string) {
  await queryClient.invalidateQueries({
    predicate: ({ queryKey }) => {
      const first = queryKey[0];
      return (
        typeof first === 'string' &&
        (first.startsWith(listPrefix(spaceId)) ||
          (first.startsWith(`/api/v1/spaces/${spaceId}/documents/`) &&
            first.endsWith('/diffusion')))
      );
    },
  });
}

/** The in-app acknowledgement of the caller; the answer is the updated transmittal. */
export function useAcknowledgeTransmittal(spaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transmittalId: string) =>
      transmittalsControllerAcknowledge(spaceId, transmittalId),
    onSuccess: async (transmittal: TransmittalDtoOutput) => {
      queryClient.setQueryData(
        getTransmittalsControllerGetQueryKey(spaceId, transmittal.id),
        transmittal,
      );
      await refreshTransmittals(queryClient, spaceId);
    },
  });
}

export interface SendVariables {
  data: CreateTransmittalDto;
  /** One key per attempt: the same payload sent again reuses it (no second number). */
  idempotencyKey: string;
}

/** Sends a transmittal (`can_manage` on the space; the API answers 404 to anyone else). */
export function useSendTransmittal(spaceId: string) {
  const { fetcher } = useRuntime();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ data, idempotencyKey }: SendVariables) => {
      const response = await fetcher<TransmittalDtoOutput>({
        method: 'POST',
        url: listPrefix(spaceId),
        data,
        idempotencyKey,
      });
      return response.data;
    },
    onSuccess: () => refreshTransmittals(queryClient, spaceId),
  });
}

/**
 * The people who can be chosen as recipients: those who already have access to the space (the
 * API refuses a person who cannot enter it, RECIPIENT_CANNOT_ENTER).
 */
export const useRecipientCandidates = (spaceId: string) =>
  useSpacePartnersControllerMembers(spaceId);
